<template>
  <div class="relative" v-click-outside="close">
    <button
      class="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm relative"
      @click.stop="toggle"
      :title="sseStatus === 'connected' ? 'Yakıt Bildirimleri (Canlı bağlı)' : 'Yakıt Bildirimleri (Bağlantı yok)'"
    >
      <i class="fas fa-bell text-base"></i>
      <!-- Canlı bağlantı göstergesi -->
      <span
        class="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-white"
        :style="{ backgroundColor: sseStatus === 'connected' ? '#22c55e' : '#9ca3af' }"
        :class="{ 'animate-pulse': sseStatus === 'connecting' }"
      ></span>
      <!-- Okunmamış bildirim sayısı -->
      <span
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 min-w-[18px] h-[18px] text-xxs font-bold text-white bg-red-500 rounded-full"
      >
        {{ unreadCount > 99 ? "99+" : unreadCount }}
      </span>
    </button>

    <!-- Dropdown -->
    <div
      v-if="open"
      class="absolute right-0 top-12 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg z-50"
      style="max-height: 480px;"
    >
      <div class="flex items-center justify-between p-3 border-b">
        <div class="text-sm font-semibold flex items-center">
          <i class="fas fa-bell mr-2 text-gray-500"></i>Yakıt Bildirimleri
        </div>
        <div class="flex items-center space-x-3">
          <button v-if="unreadCount > 0" class="text-xxs text-blue-600 hover:underline" @click="markAllRead">
            Tümünü okundu olarak işaretle
          </button>
          <button v-if="hasRead" class="text-xxs text-red-600 hover:underline" @click="deleteAllRead">
            Okunanları temizle
          </button>
        </div>
      </div>

      <div class="overflow-y-auto" style="max-height: 400px;">
        <div v-if="loading" class="text-center py-6 text-gray-400">
          <i class="fas fa-spinner fa-spin mr-1"></i>Yükleniyor
        </div>
        <div v-else-if="list.length === 0" class="text-center py-6 text-gray-400 text-xs">
          Bildirim yok
        </div>

        <div v-else>
          <div
            v-for="n in list"
            :key="n.id"
            class="flex items-start p-3 border-b hover:bg-gray-50 dark:hover:bg-gray-700 group"
            :class="{ 'bg-blue-50 dark:bg-gray-700': n.isRead !== 1 }"
          >
            <div
              class="flex flex-1 items-start cursor-pointer min-w-0"
              @click="openNotification(n)"
            >
              <div class="w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0" :style="{ backgroundColor: typeStyle(n.type).bg }">
                <i :class="typeStyle(n.type).icon" class="text-white text-xs"></i>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-xs font-semibold text-gray-800 dark:text-gray-100">{{ n.title }}</div>
                <div class="text-xxs text-gray-600 dark:text-gray-300 truncate">{{ n.message }}</div>
                <div class="text-xxs text-gray-400 mt-0.5">{{ formatDate(n.createdAt) }}</div>
              </div>
              <span v-if="n.isRead !== 1" class="w-2 h-2 bg-blue-500 rounded-full mt-1 ml-1 flex-shrink-0"></span>
            </div>
            <button
              class="ml-2 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Bildirimi sil"
              @click.stop="deleteOne(n)"
            >
              <i class="fas fa-trash text-xs"></i>
            </button>
          </div>
        </div>
      </div>

      <div v-if="list.length > 0" class="p-2 text-center border-t">
        <button class="text-xxs text-blue-600 hover:underline" @click="goToFuelRequests">
          Tüm Yakıt Taleplerini Gör →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import "moment/locale/tr";
import { fuel } from "@/networking/urlmanager";
import fuelStream from "@/services/fuelEventStream";

const clickOutside = {
  bind(el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener("click", el.__clickOutsideHandler__);
  },
  unbind(el) {
    document.removeEventListener("click", el.__clickOutsideHandler__);
  },
};

export default {
  name: "fuel-notifications",
  directives: { "click-outside": clickOutside },
  data() {
    return {
      open: false,
      loading: false,
      unreadCount: 0,
      list: [],
      sseStatus: "disconnected",
      sseUnsubs: [],
    };
  },
  computed: {
    hasRead() { return this.list.some(n => n.isRead === 1); },
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.fetchList();
        this.fetchUnreadCount();
      }
    },
    close() { this.open = false; },
    fetchUnreadCount() {
      axios.get(fuel.notificationsUnreadCount, {
        headers: { Authorization: "Bareer " + this.$store.state.userData.token },
      })
        .then((res) => { this.unreadCount = res.data?.data?.count || 0; })
        .catch(() => {});
    },
    fetchList() {
      this.loading = true;
      axios.get(fuel.notificationsList + "?page=0", {
        headers: { Authorization: "Bareer " + this.$store.state.userData.token },
      })
        .then((res) => { this.list = res.data?.data?.detail || []; })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
    markAllRead() {
      axios.post(fuel.notificationsMarkAllRead, {}, {
        headers: { Authorization: "Bareer " + this.$store.state.userData.token },
      })
        .then(() => {
          this.list = this.list.map(n => ({ ...n, isRead: 1 }));
          this.unreadCount = 0;
          this.fetchUnreadCount();
        })
        .catch(() => {});
    },
    deleteOne(n) {
      // Optimistic removal
      this.list = this.list.filter(x => x.id !== n.id);
      if (n.isRead !== 1 && this.unreadCount > 0) this.unreadCount -= 1;
      axios.post(fuel.notificationsDelete, { notificationId: n.id }, {
        headers: { Authorization: "Bareer " + this.$store.state.userData.token },
      })
        .catch(() => {
          // hata olursa yeniden yükle
          this.fetchList();
          this.fetchUnreadCount();
        });
    },
    deleteAllRead() {
      const readIds = this.list.filter(n => n.isRead === 1).map(n => n.id);
      if (readIds.length === 0) return;
      // Optimistic removal
      this.list = this.list.filter(n => n.isRead !== 1);
      axios.post(fuel.notificationsDeleteRead, {}, {
        headers: { Authorization: "Bareer " + this.$store.state.userData.token },
      })
        .catch(() => {
          this.fetchList();
          this.fetchUnreadCount();
        });
    },
    openNotification(n) {
      if (n.isRead !== 1) {
        this.$set(n, "isRead", 1);
        if (this.unreadCount > 0) this.unreadCount -= 1;
        axios.post(fuel.notificationsMarkRead, { notificationId: n.id }, {
          headers: { Authorization: "Bareer " + this.$store.state.userData.token },
        })
          .then(() => { this.fetchUnreadCount(); })
          .catch(() => { this.fetchUnreadCount(); });
      }
      this.open = false;
      if (n.requestId) {
        // İlgili talebin id'sini sakla, fuelRequests sayfası açıldığında detay modal'ı açar
        sessionStorage.setItem("openFuelRequestId", String(n.requestId));
        if (this.$route.path !== "/fuelRequests") {
          this.$router.push("/fuelRequests");
        } else {
          // Aynı sayfadaysak event ile bildir
          window.dispatchEvent(new CustomEvent("openFuelRequestDetail", { detail: { requestId: n.requestId } }));
        }
      }
    },
    goToFuelRequests() {
      this.open = false;
      if (this.$route.path !== "/fuelRequests") this.$router.push("/fuelRequests");
    },
    typeStyle(type) {
      const map = {
        new_request: { bg: "#3b82f6", icon: "fas fa-plus" },
        item_added: { bg: "#f97316", icon: "fas fa-layer-group" },
        receipt_uploaded: { bg: "#22c55e", icon: "fas fa-receipt" },
      };
      return map[type] || { bg: "#6b7280", icon: "fas fa-bell" };
    },
    formatDate(d) {
      if (!d) return "";
      return moment(d).fromNow();
    },
    setupSseListeners() {
      this.fetchUnreadCount();
      // Optimistic +1 yok — backend kullanıcıya bildirim yazmadıysa badge yalan söylemesin
      // DB write tamamlansın diye kısa gecikme sonra count + liste taze çekilir
      const onEvent = () => {
        setTimeout(() => {
          this.fetchUnreadCount();
          if (this.open) this.fetchList();
        }, 400);
      };
      this.sseUnsubs.push(fuelStream.on("new-request", onEvent));
      this.sseUnsubs.push(fuelStream.on("item-added", onEvent));
      this.sseUnsubs.push(fuelStream.on("item-approved", onEvent));
      this.sseUnsubs.push(fuelStream.on("item-rejected", onEvent));
      this.sseUnsubs.push(fuelStream.on("request-updated", onEvent));
      this.sseUnsubs.push(fuelStream.on("connected", () => this.fetchUnreadCount()));
      this.sseUnsubs.push(fuelStream.onStatus((s) => { this.sseStatus = s; }));
    },
    clearSseListeners() {
      this.sseUnsubs.forEach((fn) => { try { fn(); } catch (e) { /* noop */ } });
      this.sseUnsubs = [];
    },
  },
  mounted() { this.setupSseListeners(); },
  beforeDestroy() { this.clearSseListeners(); },
};
</script>
